import React, { useEffect, useState, useCallback } from 'react';
import { cx } from 'linaria';
import { CarouselControlButton } from 'common/components/carousel/carousel-control';
import {
  BannersCarouselControlWrapper,
  BannersCarouselControlWrapperLeft,
  BannersCarouselControlWrapperRight,
} from '../styles';
import { BannersCarouselControlProps } from '../types';

export const BannersCarouselControl: React.FC<BannersCarouselControlProps> = ({
  swiper,
  direction,
  loop,
}) => {
  const getInitialPermissions = useCallback(() => {
    if (swiper.slides.length <= 1) {
      return { prev: false, next: false };
    }

    if (loop) {
      return { prev: true, next: true };
    }

    return {
      prev: !swiper.isBeginning,
      next: !swiper.isEnd,
    };
  }, [swiper.slides.length, swiper.isBeginning, swiper.isEnd, loop]);

  const [isAllowed, setIsAllowed] = useState(getInitialPermissions());

  const updateSlidePermissions = useCallback(() => {
    setIsAllowed({
      prev: !swiper.isBeginning,
      next: !swiper.isEnd,
    });
  }, [swiper]);

  useEffect(() => {
    setIsAllowed(getInitialPermissions());

    if (!loop) {
      swiper.on('slideChange', updateSlidePermissions);
    }

    return () => swiper.off('slideChange', updateSlidePermissions);
  }, [swiper, getInitialPermissions, loop, updateSlidePermissions]);

  const handleClick = useCallback(() => {
    if (direction === 'left') {
      if (isAllowed.prev) swiper.slidePrev();
      return;
    }

    if (isAllowed.next) swiper.slideNext();
  }, [direction, isAllowed.prev, isAllowed.next, swiper]);

  const shouldRender = direction === 'left' ? isAllowed.prev : isAllowed.next;

  if (!shouldRender) return null;

  const wrapperClass =
    direction === 'left' ? BannersCarouselControlWrapperLeft : BannersCarouselControlWrapperRight;

  return (
    <div className={cx(BannersCarouselControlWrapper, wrapperClass)}>
      <CarouselControlButton
        direction={direction}
        onClick={handleClick}
        data-qa={`banners-carousel-control-${direction}`}
      />
    </div>
  );
};
